import { Checkbox, ListItemText, MenuItem } from '@mui/material';
import { FC, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledSelect } from '../../../shared';

interface Props {
    name: string;
    label: string;
    form: UseFormReturn;
    options: { label: string; value: string }[];
    multiple?: boolean;
    disabled?: boolean;
}

export const ReportSelectFilter: FC<Props> = ({ name, label, form, options, multiple = true, disabled = false }) => {
    const { t } = useTranslation();
    const currentValues = form.watch(name);

    // With only one option, that option is always selected and the filter can't be changed.
    const singleOptionValue = options.length === 1 ? options[0].value : undefined;

    useEffect(() => {
        if (singleOptionValue === undefined) return;
        if (multiple) {
            if (currentValues?.length !== 1 || currentValues[0] !== singleOptionValue) {
                form.setValue(name, [singleOptionValue]);
            }
        } else if (currentValues !== singleOptionValue) {
            form.setValue(name, singleOptionValue);
        }
    }, [singleOptionValue, currentValues, multiple, form, name]);

    const getOptionNameByValue = (value: string): string =>
        options?.find((option) => option.value === value)?.label || t('unknown');

    const formatSelectedValues = (values: string[]) =>
        options?.length > 1 && options.length === values?.length
            ? t('all')
            : values?.map(getOptionNameByValue).join(', ');

    return (
        <ControlledSelect
            name={name}
            label={label}
            multiple={multiple}
            items={options.map(({ value }) => value)}
            renderValue={multiple ? (values) => formatSelectedValues(values as string[]) : undefined}
            size="small"
            className="filter"
            disabled={disabled || singleOptionValue !== undefined}
        >
            {multiple && (
                <MenuItem value="all">
                    <Checkbox checked={currentValues?.length === (options?.length || 0)} />
                    <ListItemText primary={t('all')} />
                </MenuItem>
            )}
            {options.map((option) => (
                <MenuItem value={option.value} key={option.value}>
                    {multiple && <Checkbox checked={currentValues?.includes(option.value) || false} />}
                    <ListItemText primary={option.label} />
                </MenuItem>
            ))}
        </ControlledSelect>
    );
};
